import { Link } from "react-router-dom";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, ShieldCheck } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data?.message ||
        "Thank you for subscribing! You'll receive product updates, AI insights, and launch announcements from ATF WebWorks.",
        { duration: 6000 });
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err) || "Subscription failed. Please try again shortly.");
    }
  };

  return (
    <footer className="bg-navy border-t border-white/10 pt-20 pb-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 pb-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 border border-gold flex items-center justify-center font-heading font-bold text-gold">ATF</div>
              <span className="font-heading text-xl text-white font-semibold">WebWorks</span>
            </div>
            <p className="text-gray-400 max-w-sm leading-relaxed">
              Premium website design, templates, and complete custom builds by Ask Top Foster. Elevate your brand with a high-end digital presence.
            </p>
            <a href="mailto:support@asktopfoster.com" data-testid="footer-support-email"
              className="inline-flex items-center gap-2 mt-5 text-gray-400 hover:text-gold transition-colors">
              <Mail size={15} /> support@asktopfoster.com
            </a>
            <div className="flex items-center gap-2 mt-5 text-sm text-gray-400" data-testid="footer-veteran-founded">
              <ShieldCheck size={15} className="text-gold shrink-0" /> Proudly veteran-founded
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-5">Explore</h4>
            <ul className="space-y-3 text-gray-400">
              <li><Link to="/solutions" className="hover:text-gold transition-colors">Solutions</Link></li>
              <li><Link to="/marketplace" className="hover:text-gold transition-colors">Our Work</Link></li>
              <li><Link to="/services" className="hover:text-gold transition-colors">Services & Packages</Link></li>
              <li><Link to="/veterans" className="hover:text-gold transition-colors" data-testid="footer-veterans">Veterans Launch Initiative</Link></li>
              <li><Link to="/ai-concierge" className="hover:text-gold transition-colors" data-testid="footer-ai-concierge">AI Concierge</Link></li>
              <li><Link to="/start" className="hover:text-gold transition-colors">Start a Project</Link></li>
              <li><Link to="/#faq" className="hover:text-gold transition-colors" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/login" className="hover:text-gold transition-colors">Client Portal</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-5">Newsletter</h4>
            <p className="text-gray-400 mb-4">Get design insights and launch updates.</p>
            <form onSubmit={subscribe} className="flex" data-testid="newsletter-form">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input
                  required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="newsletter-input"
                  className="w-full bg-navy-800 border border-white/20 text-white pl-9 pr-3 py-3 text-sm focus:border-gold focus:outline-none"
                />
              </div>
              <button type="submit" data-testid="newsletter-submit" className="bg-gold text-navy px-4 hover:bg-gold-light transition-colors">
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Ask Top Foster · ATF WebWorks. All rights reserved.</p>
          <div className="flex items-center gap-5" data-testid="footer-legal">
            <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-gold transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/refund" className="hover:text-gold transition-colors" data-testid="footer-refund">Refund & Delivery</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
